import React, { useState } from "react";
import { useMonyawn } from "../../../context/MonyawnContext";
import { nowIso } from "../../../workflow";

type Props = {
  profileId: string;
};

export const ProfileCard: React.FC<Props> = ({ profileId }) => {
  const { state, patchState } = useMonyawn();
  const profile = state.candidateProfiles.find((p) => p.profile_id === profileId);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editSkills, setEditSkills] = useState("");
  const [editLevel, setEditLevel] = useState("");
  const [editStrengths, setEditStrengths] = useState("");
  const [editGaps, setEditGaps] = useState("");

  if (!profile) return null;

  function startEdit() {
    setEditSkills(profile!.skills_summary);
    setEditLevel(profile!.experience_level);
    setEditStrengths(profile!.domain_strengths);
    setEditGaps(profile!.declared_gaps);
    setEditing(true);
    setExpanded(true);
  }

  function saveEdit() {
    patchState(
      (current) => ({
        ...current,
        candidateProfiles: current.candidateProfiles.map((p) =>
          p.profile_id === profileId
            ? {
                ...p,
                skills_summary: editSkills,
                experience_level: editLevel,
                domain_strengths: editStrengths,
                declared_gaps: editGaps,
                user_corrected: true,
                updated_at: nowIso(),
              }
            : p,
        ),
      }),
      "Profile updated.",
    );
    setEditing(false);
  }

  const hasContent =
    profile.skills_summary ||
    profile.experience_level ||
    profile.domain_strengths ||
    profile.declared_gaps;

  return (
    <div className="rounded-2xl border border-brand-highlight/30 bg-gradient-to-br from-white to-brand-highlight/10 shadow-sm hover:shadow-md transition-all">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="w-2.5 h-2.5 rounded-full flex-none bg-brand-accent" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-brand-ink">Candidate Profile</p>
          <p className="text-xs text-brand-muted">
            {profile.experience_level || "Level not set"} ·{" "}
            {profile.user_corrected ? "User reviewed" : "Auto-generated"}
          </p>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-brand-highlight/40 text-brand-accent border border-brand-highlight/50">
          profile
        </span>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-brand-muted hover:text-brand-ink transition-colors ml-1"
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path
              d={expanded ? "M4 10l4-4 4 4" : "M4 6l4 4 4-4"}
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-brand-highlight/20 pt-3 space-y-3">
          {editing ? (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                  Skills
                </span>
                <textarea
                  className="w-full px-3 py-2 rounded-xl border border-black/10 bg-white text-sm focus:ring-2 focus:ring-brand-accent outline-none resize-none"
                  rows={3}
                  value={editSkills}
                  onChange={(e) => setEditSkills(e.target.value)}
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                    Level
                  </span>
                  <input
                    className="w-full px-3 py-2 rounded-xl border border-black/10 bg-white text-sm focus:ring-2 focus:ring-brand-accent outline-none"
                    value={editLevel}
                    onChange={(e) => setEditLevel(e.target.value)}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                    Strengths
                  </span>
                  <textarea
                    className="w-full px-3 py-2 rounded-xl border border-black/10 bg-white text-sm focus:ring-2 focus:ring-brand-accent outline-none resize-none"
                    rows={2}
                    value={editStrengths}
                    onChange={(e) => setEditStrengths(e.target.value)}
                  />
                </label>
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                  Gaps to cover
                </span>
                <textarea
                  className="w-full px-3 py-2 rounded-xl border border-black/10 bg-white text-sm focus:ring-2 focus:ring-brand-accent outline-none resize-none"
                  rows={3}
                  value={editGaps}
                  onChange={(e) => setEditGaps(e.target.value)}
                />
              </label>
              <div className="flex gap-2">
                <button
                  onClick={saveEdit}
                  className="bg-brand-accent text-white px-4 py-1.5 rounded-xl text-xs font-bold hover:bg-brand-accent-strong transition-all"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="bg-white border border-black/10 text-brand-ink px-4 py-1.5 rounded-xl text-xs font-bold hover:bg-black/5 transition-all"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : hasContent ? (
            <>
              {profile.skills_summary && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-brand-muted mb-1">
                    Skills
                  </p>
                  <p className="text-sm text-brand-ink leading-relaxed">{profile.skills_summary}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {profile.experience_level && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-brand-muted mb-1">
                      Level
                    </p>
                    <p className="text-sm text-brand-ink">{profile.experience_level}</p>
                  </div>
                )}
                {profile.domain_strengths && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-brand-muted mb-1">
                      Strengths
                    </p>
                    <p className="text-sm text-brand-ink leading-relaxed">
                      {profile.domain_strengths}
                    </p>
                  </div>
                )}
              </div>
              {profile.declared_gaps && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-brand-muted mb-1">
                    Gaps
                  </p>
                  <p className="text-sm text-brand-ink leading-relaxed">{profile.declared_gaps}</p>
                </div>
              )}
              <button
                onClick={startEdit}
                className="bg-white border border-black/10 text-brand-ink px-3 py-1.5 rounded-xl text-xs font-bold hover:bg-black/5 transition-all mt-1"
              >
                Edit profile
              </button>
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-brand-muted">
                Profile is empty. Fill it in so your story reads sharp.
              </p>
              <button
                onClick={startEdit}
                className="mt-3 bg-brand-accent text-white px-4 py-1.5 rounded-xl text-xs font-bold hover:bg-brand-accent-strong transition-all"
              >
                Fill in profile
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
